import React, { Component } from "react"
import { Form, Input, Button, Select, Alert } from "antd"
import { LockOutlined, GlobalOutlined } from "@ant-design/icons"
import { connect } from "react-redux"
import {
    changePhoneNumberStatus,
    changeForgotPasswordStatus,
    addPhoneNumber,
    addPassword,
    setAuthErrorMsg
} from "../stores/actions"
import { postApi } from '../../../shared/src/apis';
import { withRouter } from "react-router-dom"
import type { RouteComponentProps } from "react-router-dom"
import { Region } from '../../../shared/src/utils/commonInterfaces';
import { getRegion } from './regionWrapper';
import CountryFlag from './countryFlag';

const { Option } = Select

const CountryOption = ({ code, name }: { code: string, name: string }) => (
    <span className="login-flag-option">
        <CountryFlag code={code} />
        {name}
    </span>
)

const stripPhoneSpaces = (value: string) =>
    typeof value === "string" ? value.replace(/\s/g, "") : value

interface Props extends RouteComponentProps<any> {
    changePhoneNumberStatus: any
    changeForgotPasswordStatus: any
    postApi: any
    phoneNumber: string
    pinCode: string
    addPhoneNumber: any
    addPassword: any
    authErrorMessage: string
    setAuthErrorMsg: any
}

class LoginForm extends Component<Props> {
    region: Region = getRegion() ?? Region.EG;
    formRef = React.createRef<any>();
    state = {
        btnLoader: false,
        successNotification: true,
        errorMessage: null,
        phoneIsInvalid: true,
        submitDisable: true,
        selectedCountry: "sa",
    }

    handleCountryChange = (value: string) => {
        this.setState({ selectedCountry: value, errorMessage: null });
        if (this.formRef.current) {
            this.formRef.current.setFieldsValue({ phoneNumber: "" });
        }
    }

    onFinish = async (values: any) => {
        this.props.setAuthErrorMsg("")

        const apiBody = {
            phone: values?.phoneNumber
        }

        this.responseHandling(true, true)
        const sendOtp = await this.props.postApi(`auth/send-otp`, apiBody)

        try {
            if (sendOtp.payload.status === 200) {
                this.props.changePhoneNumberStatus(true)
                this.props.addPhoneNumber(values?.phoneNumber)
                this.props.addPassword(values?.password)
                this.responseHandling(true, false)
            } else {
                this.responseHandling(true, false)
                this.setState({ errorMessage: sendOtp.payload.message })
                console.log(this.state.errorMessage)
            }
        } catch (error) {
            this.responseHandling(false, false)
        }
    }

    responseHandling = (successNotification: any, btnLoader: boolean) => {
        this.setState({ successNotification: successNotification, btnLoader: btnLoader })
    }

    onFinishFailed = (errorInfo: any) => {
        console.log("Failed:", errorInfo)
    }

    passForgot = () => {
        this.props.changeForgotPasswordStatus(true)
    }
    
    validatePhoneNumber = async (_: any, value: string) => {
        this.setState({ errorMessage: null })
        const { selectedCountry } = this.state

        // Updated regex for local numbers only
        let egyptRegex = /^((\+?20)|0)?1[0125]\d{8}$/;
        let saudiRegex = /^((\+?966)|0)?5\d{8}$/;

        if (!value) return Promise.resolve()

        const cleanValue = value?.replace(/[\s\-()]/g, "")

        const isValid =
            selectedCountry === "eg"
                ? egyptRegex.test(cleanValue)
                : selectedCountry === "sa"
                    ? saudiRegex.test(cleanValue)
                    : false

        return isValid
            ? Promise.resolve()
            : Promise.reject(
                new Error(`Please enter a valid ${selectedCountry === "eg" ? "Egyptian" : "Saudi"} phone number.`),
            )
    }

    render() {
        const { btnLoader, errorMessage, selectedCountry } = this.state
        const { authErrorMessage } = this.props
        const placeholder = selectedCountry === "sa" ? "05XXXXXXXX" : "01XXXXXXXX"
        const formError = errorMessage || authErrorMessage

        return (
            <div className="login-form">
                <Form
                    ref={this.formRef}
                    name="basic"
                    layout="vertical"
                    size="large"
                    requiredMark={false}
                    initialValues={{ remember: true }}
                    onFinish={this.onFinish}
                    onFinishFailed={this.onFinishFailed}
                >
                    {this.region === Region.SE && (
                        <Form.Item label="Country">
                            <Select
                                value={selectedCountry}
                                onChange={this.handleCountryChange}
                                suffixIcon={<GlobalOutlined />}
                                dropdownClassName="login-country-dropdown"
                            >
                                <Option value="sa">
                                    <CountryOption code="sa" name="Saudi Arabia" />
                                </Option>
                                <Option value="eg">
                                    <CountryOption code="eg" name="Egypt" />
                                </Option>
                            </Select>
                        </Form.Item>
                    )}
                    {this.region === Region.SE ? (
                        <Form.Item
                            label="Phone number"
                            name="phoneNumber"
                            normalize={stripPhoneSpaces}
                            rules={[
                                { required: true, message: "Please enter a phone number." },
                                { validator: this.validatePhoneNumber },
                            ]}
                        >
                            <Input
                                prefix={<CountryFlag code={selectedCountry} />}
                                placeholder={placeholder}
                                autoComplete="tel"
                                onChange={() => this.setState({ errorMessage: null })}
                            />
                        </Form.Item>)
                        : (
                            <Form.Item
                            label="Phone number"
                            name="phoneNumber"
                            normalize={stripPhoneSpaces}
                            rules={[
                                { required: true, message: 'Please enter a phone number.' },
                                { pattern: /^((\+?20)|0)?1[0125]\d{8}$/, message: 'Please enter a valid phone number.', validateTrigger: 'onSubmit' },
                            ]}
                        >
                            <Input
                                prefix={<CountryFlag code="eg" />}
                                placeholder="Mobile Number"
                                autoComplete="tel"
                                onChange={() => this.setState({ errorMessage: null })}
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        label="Password"
                        name="password"
                        rules={[{ required: true, message: 'Please enter your password!' }]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Password"
                            autoComplete="current-password"
                            onChange={() => this.setState({ errorMessage: null })}
                        />
                    </Form.Item>

                    {formError && (
                        <Alert className="login-alert" type="error" showIcon message={formError} />
                    )}

                    <div className="login-actions">
                        <span className="passForgot" onClick={this.passForgot}>
                            Forgot password?
                        </span>
                    </div>
                    <Button type="primary" htmlType="submit" loading={btnLoader} block>
                        Log In
                    </Button>
                </Form>
            </div>
        )
    }
}

const mapStateToProp = (state: any) => ({
    pinCode: state.auth.pinCode,
    phoneNumber: state.auth.phoneNumber,
    authErrorMessage: state.auth.authErrorMessage,
})

export default withRouter(
    connect(mapStateToProp, {
        changeForgotPasswordStatus,
        changePhoneNumberStatus,
        postApi,
        addPhoneNumber,
        addPassword,
        setAuthErrorMsg,
    })(LoginForm),
)
